import { addMinutes, subMinutes, subHours } from 'date-fns'

export type VehicleType = 'Small' | 'Large' | 'Trailer'
export type SLAStatus = 'Under 3h' | 'Approaching' | 'Overdue'
export type GateName = 'Main Gate' | 'Truck Gate' | 'Visitor Gate'

export interface Vehicle {
  id: string
  plate: string
  type: VehicleType
  entryTime: Date
  exitTime?: Date
  gate: GateName
  visitCount: number
  hasWarning: boolean
  warningReason?: string
  driverName: string
  confidence: number
  bay?: string
  status: 'Inside' | 'Exited'
  thumbnailUrl: string
}

export interface Guard {
  id: string
  name: string
  photoUrl: string
  onDutySince: Date
  scheduledEnd: Date
  status: 'On Duty' | 'On Break' | 'Missing Exit'
  lastCheckIn: Date
}

export interface Alert {
  id: string
  type: 'Pedestrian' | 'Smoking' | 'Congestion'
  zone: string
  timestamp: Date
  severity: 'Medium' | 'High'
  count?: number
  details: string
}

const NOW = new Date()

export const MOCK_VEHICLES: Vehicle[] = [
  {
    id: 'v1',
    plate: 'UP32TA2726',
    type: 'Trailer',
    entryTime: subHours(NOW, 4.5), // Overdue
    gate: 'Truck Gate',
    visitCount: 12,
    hasWarning: true,
    warningReason: 'Speeding on 12 Jan',
    driverName: 'Rajesh Kumar',
    confidence: 98,
    bay: 'Bay 2',
    status: 'Inside',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=400&q=80',
  },
  {
    id: 'v2',
    plate: 'HR26DK5678',
    type: 'Large',
    entryTime: subHours(NOW, 3.5), // Approaching
    gate: 'Main Gate',
    visitCount: 5,
    hasWarning: false,
    driverName: 'Amit Singh',
    confidence: 95,
    bay: 'Bay 1',
    status: 'Inside',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1586191582114-091fcf18c8fd?w=400&q=80',
  },
  {
    id: 'v3',
    plate: 'DL8CAF4321',
    type: 'Small',
    entryTime: subMinutes(NOW, 45), // Under 3h
    gate: 'Visitor Gate',
    visitCount: 1,
    hasWarning: false,
    driverName: 'Vikram Malhotra',
    confidence: 99,
    status: 'Inside',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=400&q=80',
  },
  {
    id: 'v4',
    plate: 'MH12DE1234',
    type: 'Trailer',
    entryTime: subHours(NOW, 2),
    gate: 'Truck Gate',
    visitCount: 8,
    hasWarning: true,
    warningReason: 'Documentation incomplete',
    driverName: 'Suresh Patel',
    confidence: 92,
    bay: 'Bay 3',
    status: 'Inside',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=400&q=80',
  },
  {
    id: 'v5',
    plate: 'KA05MJ9988',
    type: 'Large',
    entryTime: subHours(NOW, 5),
    exitTime: subMinutes(NOW, 10),
    gate: 'Truck Gate',
    visitCount: 22,
    hasWarning: false,
    driverName: 'Mohammed Rafiq',
    confidence: 97,
    status: 'Exited',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1586191582114-091fcf18c8fd?w=400&q=80',
  },
  {
    id: 'v6',
    plate: 'TN09BA7766',
    type: 'Small',
    entryTime: subHours(NOW, 1),
    gate: 'Main Gate',
    visitCount: 3,
    hasWarning: false,
    driverName: 'John Thomas',
    confidence: 96,
    status: 'Inside',
    thumbnailUrl:
      'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=400&q=80',
  },
]

export const MOCK_GUARDS: Guard[] = [
  {
    id: 'g1',
    name: 'Sandeep Singh',
    photoUrl:
      'https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&q=80',
    onDutySince: subHours(NOW, 6),
    scheduledEnd: addMinutes(NOW, 120),
    status: 'On Duty',
    lastCheckIn: subMinutes(NOW, 15),
  },
  {
    id: 'g2',
    name: 'Priya Sharma',
    photoUrl:
      'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&q=80',
    onDutySince: subHours(NOW, 2),
    scheduledEnd: addMinutes(NOW, 360),
    status: 'On Duty',
    lastCheckIn: subMinutes(NOW, 5),
  },
  {
    id: 'g3',
    name: 'Rahul Verma',
    photoUrl:
      'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=400&q=80',
    onDutySince: subHours(NOW, 9), // Shift ended 1h ago
    scheduledEnd: subHours(NOW, 1),
    status: 'Missing Exit',
    lastCheckIn: subHours(NOW, 2),
  },
]

export const MOCK_ALERTS: Alert[] = [
  {
    id: 'a1',
    type: 'Pedestrian',
    zone: 'Restricted Zone B',
    timestamp: subMinutes(NOW, 2),
    severity: 'High',
    count: 2,
    details: '2 unauthorized personnel detected near fuel depot',
  },
  {
    id: 'a2',
    type: 'Smoking',
    zone: 'Loading Bay Exterior',
    timestamp: subMinutes(NOW, 15),
    severity: 'Medium',
    details: 'Smoke signature detected near Bay 1',
  },
  {
    id: 'a3',
    type: 'Congestion',
    zone: 'Main Gate Approach',
    timestamp: subMinutes(NOW, 5),
    severity: 'Medium',
    details: 'Queue length > 5 vehicles for 10 mins',
  },
]

export const BAY_DATA = [
  { time: '08:00', occupancy: 20 },
  { time: '10:00', occupancy: 45 },
  { time: '12:00', occupancy: 80 },
  { time: '14:00', occupancy: 65 },
  { time: '16:00', occupancy: 90 },
  { time: '18:00', occupancy: 50 },
]
